import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AreaAutocomplete } from '@/components/AreaSelector';
import JournalAutocomplete from '@/components/JournalAutocomplete';

const QUALIS_OPTIONS = ['A1', 'A2', 'A3', 'A4', 'B1', 'B2', 'B3', 'B4', 'B5', 'C'];

const SuggestCallPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    journal_name: '',
    issn: '',
    qualis_classification: 'B1',
    knowledge_areas: [],
    deadline: '',
    continuous_flow: false,
    is_free: true,
    submission_url: '',
    additional_info: ''
  });

  const handleAreasChange = (newAreas) => {
    setFormData(prev => ({
      ...prev,
      knowledge_areas: newAreas
    }));
  };

  const handleJournalSelect = (journal) => {
    // Auto-fill ISSN and QUALIS when selecting an existing journal
    setFormData(prev => ({
      ...prev,
      journal_name: journal.name,
      issn: journal.issn || prev.issn,
      qualis_classification: journal.qualis || prev.qualis_classification
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.knowledge_areas.length === 0) {
      toast.error('Selecione pelo menos uma área de conhecimento');
      return;
    }

    if (!formData.continuous_flow && !formData.deadline) {
      toast.error('Informe o prazo ou marque como fluxo contínuo');
      return;
    }

    setSubmitting(true);

    try {
      // Prepare data for submission
      const submitData = {
        ...formData,
        // Add time component to deadline when submitting
        deadline: formData.deadline && !formData.continuous_flow 
          ? formData.deadline + 'T23:59:59Z' 
          : null
      };
      
      const response = await api.post('/api/call-suggestions', submitData);
      
      if (response.data.has_duplicate) {
        toast.warning('Sugestão enviada! ⚠️ Possível chamada duplicada foi detectada.');
      } else {
        toast.success('Sugestão enviada com sucesso! Aguarde aprovação do admin.');
      }
      
      navigate('/my-suggestions');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar sugestão');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      <header className="bg-white border-b border-[#E4E4E7]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button
            data-testid="back-btn"
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">Sugerir Chamada Para Publicação</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6 flex gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-1">Sobre sugestões de chamadas:</p>
            <p>Sua sugestão será analisada por um administrador antes de ser publicada. Você receberá uma notificação com a decisão.</p>
          </div>
        </div>

        <div className="bg-white border border-[#E4E4E7] rounded-md p-6">
          <form data-testid="suggest-call-form" onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Título da Chamada *</Label>
              <Input
                data-testid="title-input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Chamada para Artigos em Biotecnologia"
                required
                className="mt-2"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Nome da Revista/Congresso *</Label>
                <JournalAutocomplete
                  value={formData.journal_name}
                  onChange={(value) => setFormData({ ...formData, journal_name: value })}
                  onJournalSelect={handleJournalSelect}
                  placeholder="Digite para buscar revistas existentes..."
                  className="mt-2"
                />
                <p className="text-xs text-[#4B4C52] mt-1">
                  Sugestões aparecem ao digitar. ISSN é preenchido automaticamente.
                </p>
              </div>

              <div>
                <Label>ISSN</Label>
                <Input
                  data-testid="issn-input"
                  value={formData.issn}
                  onChange={(e) => {
                    // Format ISSN: XXXX-XXXX
                    let value = e.target.value.replace(/[^\dXx]/g, '').toUpperCase();
                    if (value.length > 4) {
                      value = value.slice(0, 4) + '-' + value.slice(4, 8);
                    }
                    setFormData({ ...formData, issn: value });
                  }}
                  placeholder="0000-0000"
                  maxLength={9}
                  className="mt-2"
                />
                <p className="text-xs text-[#4B4C52] mt-1">Preenchido automaticamente ao selecionar revista</p>
              </div>
            </div>

            <div>
              <Label>Descrição *</Label>
              <Textarea
                data-testid="description-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descreva a chamada, temas aceitos, requisitos..."
                rows={4}
                required
                className="mt-2"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Classificação QUALIS *</Label>
                <select
                  data-testid="qualis-select"
                  value={formData.qualis_classification}
                  onChange={(e) => setFormData({ ...formData, qualis_classification: e.target.value })}
                  className="mt-2 w-full border border-[#E4E4E7] rounded-md p-2"
                  required
                >
                  {QUALIS_OPTIONS.map(q => (
                    <option key={q} value={q}>{q}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Gratuidade</Label>
                <select
                  data-testid="free-select"
                  value={formData.is_free}
                  onChange={(e) => setFormData({ ...formData, is_free: e.target.value === 'true' })}
                  className="mt-2 w-full border border-[#E4E4E7] rounded-md p-2"
                >
                  <option value="true">Gratuito</option>
                  <option value="false">Pago</option>
                </select>
              </div>
            </div>

            <div>
              <AreaAutocomplete
                value={formData.knowledge_areas}
                onChange={handleAreasChange}
                label="Áreas de Conhecimento *"
                maxSelections={5}
                placeholder="Digite para buscar áreas específicas (ex: sociologia, química, direito...)"
              />
              <p className="text-xs text-gray-500 mt-1">
                Selecione áreas específicas para que sua chamada alcance pesquisadores com interesses compatíveis.
              </p>
            </div>

            <div>
              <label className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  checked={formData.continuous_flow}
                  onChange={(e) => setFormData({ ...formData, continuous_flow: e.target.checked, deadline: '' })}
                />
                <span className="text-sm font-semibold">Fluxo Contínuo (sem prazo definido)</span>
              </label>

              {!formData.continuous_flow && (
                <div>
                  <Label>Prazo (Deadline) *</Label>
                  <Input
                    data-testid="deadline-input"
                    type="date"
                    value={formData.deadline}
                    onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                    required={!formData.continuous_flow}
                    className="mt-2 max-w-xs"
                  />
                </div>
              )}
            </div>

            <div>
              <Label>URL de Submissão</Label>
              <Input
                data-testid="url-input"
                type="url"
                value={formData.submission_url}
                onChange={(e) => setFormData({ ...formData, submission_url: e.target.value })}
                placeholder="https://..."
                className="mt-2"
              />
            </div>

            <div>
              <Label>Informações Adicionais</Label>
              <Textarea
                data-testid="additional-input"
                value={formData.additional_info}
                onChange={(e) => setFormData({ ...formData, additional_info: e.target.value })}
                placeholder="Taxa de publicação, requisitos especiais, etc."
                rows={3}
                className="mt-2"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                data-testid="submit-btn"
                type="submit"
                disabled={submitting}
                className="bg-[#002FA7] hover:bg-[#001f75]"
              >
                {submitting ? 'Enviando...' : 'Enviar Sugestão'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard')}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SuggestCallPage;